/**
 * USCIS 508 Compliant Colors - Updated for ColorDemo compatibility
 */

export const USCIS_COLORS = {
  // Primary USCIS Brand Colors
  primary: '#003366',      // USCIS Blue - 7:1 contrast ratio
  secondary: '#B31B1B',    // USCIS Red - 4.8:1 contrast ratio

  // Semantic Colors (WCAG AA Compliant)
  success: '#2E7D32',      // Success Green - 5.2:1 contrast ratio
  warning: '#F57C00',      // Warning Orange - 4.7:1 contrast ratio
  error: '#D32F2F',        // Error Red - 4.9:1 contrast ratio
  info: '#1976D2',         // Info Blue - 5.1:1 contrast ratio

  // Neutral Colors for Text & Backgrounds
  neutral: {
    50: '#FAFAFA',         // Very light gray
    100: '#F5F5F5',        // Light gray background
    200: '#EEEEEE',        // Border gray
    300: '#E0E0E0',        // Disabled elements
    400: '#BDBDBD',        // Secondary text
    500: '#9E9E9E',        // Icons
    600: '#757575',        // Secondary text darker
    700: '#616161',        // Primary text
    800: '#424242',        // Darker text
    900: '#212121',        // Very dark text
  },

  // High Contrast Mode Colors
  highContrast: {
    background: '#000000',  // Pure black background
    text: '#FFFFFF',       // Pure white text
    border: '#FFFFFF',      // White borders
  }
} as const;

// USCIS Compliant Colors with contrast text for ColorDemo
export const USCIS_COMPLIANT_COLORS = {
  primary: {
    main: '#003366',
    light: '#4A5A73',
    dark: '#001A33',
    contrastText: '#FFFFFF'
  },
  secondary: {
    main: '#B31B1B',
    light: '#C74747',
    dark: '#7A1212',
    contrastText: '#FFFFFF'
  },
  success: {
    main: '#2E7D32',
    light: '#5A8F5D',
    dark: '#1B4F1E',
    contrastText: '#FFFFFF'
  },
  warning: {
    main: '#F57C00',
    light: '#F7A247',
    dark: '#B25500',
    contrastText: '#000000'
  },
  error: {
    main: '#D32F2F',
    light: '#E05A5A',
    dark: '#8F1F1F',
    contrastText: '#FFFFFF'
  },
  info: {
    main: '#1976D2',
    light: '#4A9BE7',
    dark: '#0F4F99',
    contrastText: '#FFFFFF'
  },
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  chart: {
    1: '#003366',
    2: '#1976D2',
    3: '#2E7D32',
    4: '#F57C00',
    5: '#D32F2F',
    6: '#7B1FA2',
    7: '#FF9800',
    8: '#757575',
  }
};

export const DOMAIN_COLORS = {
  'Business Domain': '#003366',
  'Technical Domain': '#1976D2',
  'Data Domain': '#2E7D32',
  'Application Domain': '#F57C00',
  'Infrastructure Domain': '#757575',
  'Regulatory Domain': '#D32F2F',
  'Customer Domain': '#7B1FA2',
  'Product Domain': '#FF9800',
};

export const STATUS_COLORS = {
  'Active': '#2E7D32',
  'Inactive': '#757575',
  'Under Development': '#F57C00',
  'Deprecated': '#D32F2F',
  'Draft': '#FF9800',
  'Archived': '#424242',
};

export const PRIORITY_COLORS = {
  'Critical': '#D32F2F',
  'High': '#F57C00',
  'Medium': '#FF9800',
  'Low': '#2E7D32',
};

// Color contrast utility
export const getContrastText = (backgroundColor: string): string => {
  // Basic luminance calculation
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

// Accessibility utility functions - no parameters needed
export const getFocusStyle = () => ({
  '&:focus': {
    outline: '3px solid #005fcc',
    outlineOffset: '2px',
  },
});

export const getAccessibleButtonStyles = (color?: string) => ({
  minHeight: '44px',
  minWidth: '44px',
  '&:focus': {
    outline: '3px solid #005fcc',
    outlineOffset: '2px',
  },
});

export const getAccessibleTextStyles = (variant?: string) => ({
  '&:focus': {
    outline: '2px solid #005fcc',
    outlineOffset: '2px',
  },
});

// Export individual colors for easy access
export const USCIS_BLUE = USCIS_COLORS.primary;
export const USCIS_RED = USCIS_COLORS.secondary;

export default USCIS_COLORS;
